import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

// Shared by Home.jsx (Global mode: /home, /services) and ServicesLayout.jsx (Service mode:
// /myFpo, /mybusiness, ...) — SIDENAV_REDESIGN_PLAN_V2.md's responsive pass originally lived only
// in Home.jsx, which meant the hamburger/collapse feature silently didn't exist on the actual
// service pages most users spend their time on. Extracted here so both layouts share one
// implementation and can't drift out of sync with each other again.
//
// One toggle, two behaviors depending on viewport: below lg it's an off-canvas drawer
// (isMobileSidebarOpen); at/above lg it's an icon-rail collapse (isDesktopCollapsed). Only one of
// the two is ever meaningful at a given width — the resize listener resets whichever doesn't
// apply so they can't desync across a breakpoint crossing.
export function useSidebarToggle() {
  const location = useLocation();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(() => {
    try {
      // Gate the persisted preference by the *current* viewport, not just whatever it was last
      // set at — otherwise a `true` saved from a prior desktop session flashes the 72px icon-rail
      // width into the very first mobile paint (before the resize-correction effect below has a
      // chance to run), which is exactly what "collapsed rail bleeding into the mobile drawer"
      // looks like: short labels wrapping onto two lines at a width that should easily fit them.
      if (window.innerWidth < 992) return false;
      return localStorage.getItem("feed_sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("feed_sidebar_collapsed", isDesktopCollapsed ? "1" : "0");
    } catch {
      // Storage can throw (private browsing, quota) — collapse state just won't persist.
    }
  }, [isDesktopCollapsed]);

  // Close the mobile drawer automatically on navigation — otherwise it stays open over the new
  // page after tapping a link, which reads as broken rather than intentional.
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsMobileSidebarOpen(false);
      } else {
        setIsDesktopCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggle = useCallback(() => {
    if (window.innerWidth >= 992) {
      setIsDesktopCollapsed((collapsed) => !collapsed);
    } else {
      setIsMobileSidebarOpen((open) => !open);
    }
  }, []);

  const isExpanded = window.innerWidth >= 992 ? !isDesktopCollapsed : isMobileSidebarOpen;

  return {
    isMobileSidebarOpen,
    setIsMobileSidebarOpen,
    isDesktopCollapsed,
    toggle,
    isExpanded,
  };
}
